/**
 * TopBar — sticky top chrome for the YINSH multiplayer game screen.
 *
 * Replaces the old stacked "header + waiting banner" pattern. Holds:
 *  - back link (calls onLeave)
 *  - title
 *  - phase badge (derived from gameState.phase)
 *  - connection-status pill
 */

import type { GamePhase } from "@cloudnest/yinsh-engine";

interface TopBarProps {
  readonly phase: GamePhase;
  readonly currentPlayerName: string;
  readonly connectionStatus: "connected" | "reconnecting";
  readonly onLeave: () => void;
  readonly onForfeit?: () => void;
}

const PHASE_LABEL: Record<GamePhase, string> = {
  RING_PLACEMENT: "링 배치",
  PLAYING: "이동",
  REMOVING_ROW: "점수",
  GAME_END: "게임 종료",
};

export function TopBar({
  phase,
  currentPlayerName,
  connectionStatus,
  onLeave,
  onForfeit,
}: TopBarProps) {
  const isConnected = connectionStatus === "connected";
  const phaseLabel =
    phase === "GAME_END"
      ? PHASE_LABEL[phase]
      : `${PHASE_LABEL[phase]} · ${currentPlayerName} 차례`;
  const canForfeit = phase !== "GAME_END" && onForfeit !== undefined;

  return (
    <header
      data-testid="game-top-bar"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        background: "var(--cn-surface-primary)",
        borderBottom: "1px solid var(--cn-border-subtle)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <button
        type="button"
        onClick={onLeave}
        data-testid="game-leave-button"
        style={{
          background: "transparent",
          border: "none",
          color: "var(--cn-accent-blue)",
          cursor: "pointer",
          padding: "4px 6px",
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        ← 뒤로
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 700,
          color: "var(--cn-text-primary)",
          letterSpacing: "0.02em",
        }}
      >
        YINSH
      </h1>

      <span
        data-testid="phase-badge"
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: "4px 10px",
          borderRadius: 999,
          background: "var(--cn-yinsh-phase-bg)",
          color: "var(--cn-yinsh-phase-text)",
          border: "1px solid var(--cn-yinsh-phase-border)",
        }}
      >
        {phaseLabel}
      </span>

      <div style={{ flex: 1 }} />

      {canForfeit && (
        <button
          type="button"
          onClick={onForfeit}
          data-testid="forfeit-button"
          style={{
            background: "transparent",
            border: "1px solid var(--cn-status-error, #c0392b)",
            color: "var(--cn-status-error, #c0392b)",
            cursor: "pointer",
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 999,
          }}
        >
          기권
        </button>
      )}

      <span
        data-testid="connection-status"
        data-status={connectionStatus}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 500,
          padding: "4px 10px",
          borderRadius: 999,
          background: isConnected
            ? "var(--cn-status-success-bg, var(--cn-accent-green-bg))"
            : "var(--cn-status-error-bg)",
          color: isConnected
            ? "var(--cn-accent-green, var(--cn-status-success))"
            : "var(--cn-status-error)",
          border: `1px solid ${
            isConnected
              ? "var(--cn-accent-green, var(--cn-status-success))"
              : "var(--cn-status-error)"
          }`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: 999,
            background: isConnected
              ? "var(--cn-accent-green, var(--cn-status-success))"
              : "var(--cn-status-error)",
          }}
        />
        {isConnected ? "연결됨" : "재연결 중…"}
      </span>
    </header>
  );
}
